import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  COMPANY_QUALITIES_NAMES,
  COMPETITIVE_ANALYSIS_NAMES,
  COMPETITORS_COMPARISON_NAMES,
  FEATURES_FOR_COMPARISON_NAMES,
  FEATURES_LIST_NAMES,
  KEY_COMPETITORS_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import { B2B_NAMES, B2C_NAMES, CONSUMER_NEEDS_NAMES } from 'components/BusinessToolForm/Sections/MarketAnalysis/ConsumerNeeds/utils/form';
import {
  AVERAGE_PRICES_CHART_NAMES,
  CONSUMER_DETAILS_NAMES,
  CURRENT_MARKET_STRUCTURE_NAMES,
  FORECASTED_CONSUMPTION_CHART_NAMES,
  MARKET_ANALYSIS_OVERVIEW_NAMES,
  MARKET_TRENDS_FIELD_NAMES,
  REGION_PERIODS_NAMES,
  REGIONS_PRODUCT_NAMES,
} from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';

import { ul } from '../helpers';

const YEARS_ROW_INSTRUCTIONS = {
  name: 'Wert',
  instruction: 'Geben Sie den Marktwert an und stellen Sie sicher, dass er korrekt und klar ist.',
};

const CONSUMPTION_TABLE_DATA_INSTRUCTIONS = {
  [REGION_PERIODS_NAMES.Y1]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y2]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y3]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y4]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y5]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y6]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.REGION]: {
    name: 'Name',
    instruction: 'Geben Sie den exakten Namen des Marktes an. Stellen Sie sicher, dass er korrekt und klar ist.',
    exampleOne: 'USA',
    exampleTwo: 'Europa',
  },
};

const FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS = {
  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: {
    name: 'Prognostizierter Verbrauch',
    instruction: 'Geben Sie einen klaren und präzisen Titel an, der den Inhalt des Diagramms korrekt widerspiegelt.',
    exampleOne: 'Europäischer Markt für Finanzdienstleistungen',
    exampleTwo: 'Amerikanischer Markt für landwirtschaftliche Geräte',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.REGION]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS]: CONSUMPTION_TABLE_DATA_INSTRUCTIONS,
  [FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE]: {
    name: 'Datenquelle',
    instruction: 'Geben Sie die zuverlässige Quelle oder die verwendete Methode zur Datenerhebung klar an.',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const CONSUMER_DETAILS_INSTRUCTIONS = {
  [CONSUMER_DETAILS_NAMES.CONSUMER]: {
    name: 'Konsument',
    instruction: 'Geben Sie den exakten Namen des Segments an. Stellen Sie sicher, dass er korrekt und klar ist.',
    exampleOne: 'USA\nEuropa\nAsien',
    exampleTwo: 'Landwirte\nAgro-Unternehmen',
  },
  [CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE]: {
    name: 'Prozentualer Anteil',
    instruction:
      'Geben Sie den Anteil jedes Segments an. Stellen Sie sicher, dass die Summe aller Segmente 100 % ergibt.',
    exampleOne: '',
    exampleTwo: '',
  },
  [CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE]: {
    name: 'Physischer Anteil',
    instruction:
      'Geben Sie den Anteil jedes Segments in physischen Einheiten an. Stellen Sie klare und präzise Werte bereit.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const MARKET_TRENDS_INSTRUCTIONS = {
  [MARKET_TRENDS_FIELD_NAMES.TREND_NAME]: {
    name: 'Trendname',
    instruction:
      'Identifizieren Sie die wichtigsten Markttrends. Stellen Sie sicher, dass sie relevant sind und die aktuellen Marktdynamiken widerspiegeln.',
    exampleOne: 'Fokus auf Nachhaltigkeit',
    exampleTwo: 'Spezialisierung auf Nischenbranchen',
  },
  [MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]: {
    name: 'Trendanalyse',
    instruction:
      'Geben Sie einen Überblick über aktuelle Markttrends in Ihrer Branche. Betonen Sie relevante Veränderungen, Präferenzen oder Verhaltensmuster, die den Markt beeinflussen.',
    exampleOne:
      'Hersteller entwickeln umweltfreundliche Geräte mit reduzierten Emissionen. Zudem steigt die Nachfrage nach Maschinen, die nachhaltige Landwirtschaftspraktiken unterstützen.',
    exampleTwo:
      'Es gibt einen wachsenden Trend zur Spezialisierung in Nischenbereichen wie juristischen, medizinischen, technischen oder Marketingübersetzungen.',
  },
};

const CURRENT_MARKET_STRUCTURE_INSTRUCTIONS = {
  [CURRENT_MARKET_STRUCTURE_NAMES.TITLE]: {
    name: 'Diagrammtitel',
    instruction: 'Geben Sie einen klaren und präzisen Titel an, der den Inhalt des Diagramms korrekt widerspiegelt.',
    exampleOne: 'Geografische Marktstruktur',
    exampleTwo: 'Marktstruktur nach Konsumenten',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER]: {
    name: '',
    instruction: 'Geben Sie den genauen Namen des Segments an. Stellen Sie sicher, dass er korrekt und klar ist.',
    exampleOne: `USA
    Europa
    Asien`,
    exampleTwo: `Landwirte
    Agro-Unternehmen`,
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.DATE]: {
    name: '',
    instruction: 'Geben Sie das genaue Datum an, einschließlich Monat und Jahr.',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS]: CONSUMER_DETAILS_INSTRUCTIONS,
  [CURRENT_MARKET_STRUCTURE_NAMES.SOURCE]: {
    name: 'Datenquelle',
    instruction: 'Geben Sie die zuverlässige Quelle oder die verwendete Methode zur Datenerhebung klar an.',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const PRODUCT_ROW_INSTRUCTIONS = {
  name: 'Produkt/Dienstleistung',
  instruction:
    'Geben Sie den exakten Namen des Produkts oder der Dienstleistung an. Stellen Sie sicher, dass er korrekt ist und das Angebot eindeutig widerspiegelt.',
  exampleOne: 'Ketten-Traktoren',
  exampleTwo: 'KI-gestützte Übersetzungswerkzeuge',
};

const AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS = {
  [REGIONS_PRODUCT_NAMES.PRODUCT]: PRODUCT_ROW_INSTRUCTIONS,
};

const MARKET_OVERVIEW_INSTRUCTIONS = {
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW]: {
    name: 'Marktsegmentierung',
    instruction:
      'Definieren Sie den betrachteten Markt und beschreiben Sie wichtige Merkmale und Marktsegmente. Gehen Sie auch auf Markteintrittsbarrieren und Herausforderungen ein.',
    exampleOne:
      'Der betrachtete Markt ist der Sektor für landwirtschaftliche Maschinen. Er ist segmentiert nach Gerätetypen: Traktoren, Pfluggeräte, Sämaschinen und Erntemaschinen.',
    exampleTwo:
      'Der Markt für Sprachdienstleistungen bedient Kunden und Übersetzer weltweit. Er umfasst schriftliche Übersetzungen, Dolmetschen, Untertitelung und Voice-over.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY]: {
    name: 'Produktübersicht',
    instruction:
      'Beschreiben Sie die wichtigsten Dienstleistungen oder Produkte im Zielmarkt. Welche Merkmale, Einsatzgebiete und Preisstrategien prägen die Nachfrage?',
    exampleOne:
      'Das Angebot umfasst Traktoren der Klassen A und B. Klasse A legt Wert auf Leichtbau, Klasse B auf Zuverlässigkeit und Geländegängigkeit.',
    exampleTwo:
      'Dienstleistungen umfassen menschliche, maschinelle und KI-gestützte Übersetzungen mit unterschiedlichen Preisstrukturen.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION]: {
    name: 'Konsumentendemografie',
    instruction:
      'Fassen Sie Demografie, Verhalten und Präferenzen der Konsumenten zusammen. Nutzen Sie verfügbare Daten für Nachfrageanalysen.',
    exampleOne:
      'Die Konsumenten sind Landwirte weltweit, besonders in Regionen mit schweren Böden. Die Nachfrage schwankt saisonal.',
    exampleTwo:
      'Die Konsumenten reichen von Privatpersonen bis zu Unternehmen, besonders in globalen Wirtschaftszentren und Regionen mit hohen Migrationsraten.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS]: {
    name: 'Identifizieren Sie Ihre Wettbewerber',
    instruction:
      'Identifizieren Sie Ihre Wettbewerber im Markt anhand ihrer Anzahl und heben Sie die Marktführer und deren Positionierung hervor. Analysieren Sie Stärken und Schwächen sowie Marktanteile, um das Wettbewerbsumfeld zu verstehen.',
    exampleOne:
      "Der Markt umfasst ca. 2.000 Traktorenhersteller, wobei John Deere mit einem Marktanteil von 20 % führend ist. John Deeres Positionierung betont Zuverlässigkeit und Innovation. Die Wettbewerber unterscheiden sich in Größe und Spezialisierung und weisen jeweils spezifische Stärken und Schwächen auf.",
    exampleTwo:
      'Es gibt 15 Sprachdienstleistungsplattformen auf dem Markt, wobei T1 und STL führend sind. T1 hat aufgrund seines umfangreichen Übersetzernetzwerks und seiner benutzerfreundlichen Oberfläche den größten Marktanteil. STL differenziert sich durch die Integration fortschrittlicher Technologien und personalisierten Kundenservice.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION]: {
    name: 'Verbrauchsanalyse',
    instruction:
      'Beschreiben Sie die Verbrauchstrends anhand von Statistiken. Geben Sie Ist-Daten und Prognosezahlen an und heben Sie Wachstumstrends und Marktdynamiken hervor.',
    exampleOne:
      'Im Jahr 2022 wurden 1 Million Raupentraktoren verkauft, mit einer erwarteten jährlichen Wachstumsrate von 5–7 %. Dieses Wachstum spiegelt die zunehmende Mechanisierung in der Landwirtschaft und die steigende Nachfrage nach zuverlässigen Traktoren mit geringer Umweltbelastung wider.',
    exampleTwo:
      'Laut einem Bericht des Beratungsunternehmens A wird der Gesamtmarkt für Sprachdienstleistungen im Jahr 2024 auf 54 Milliarden US-Dollar geschätzt. Es wird prognostiziert, dass der Markt für Sprachdienstleistungen mit einer durchschnittlichen jährlichen Wachstumsrate (CAGR) von 17 % wachsen und bis 2027 78 Milliarden US-Dollar erreichen wird. Die Wachstumstrends zeigen einen stetigen Anstieg der Nachfrage nach Sprachdienstleistungen.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART]: FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS,

  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE]: {
    name: 'Marktanteil',
    instruction:
      'Analysieren Sie Verkaufsdaten, um den aktuellen Marktanteil zu bestimmen. Heben Sie Faktoren wie Markenloyalität und Innovationskraft hervor.',
    exampleOne:
      'Der Marktanteil des Unternehmens beträgt 0,02 %. Dank innovativer Kettentraktoren verfügt es über eine starke Position.',
    exampleTwo:
      'Das Unternehmen hält 15 % Marktanteil im Sprachdienstleistungssektor und ist für hohe Qualität und starke Marke bekannt.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS]: MARKET_TRENDS_INSTRUCTIONS,

  [MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD]: {
    name: 'Vertriebsstrategie',
    instruction:
      'Beschreiben Sie Vertriebskanäle wie Online-Plattformen, Retail-Partnerschaften und Direktvertrieb.',
    exampleOne:
      'Typische Kanäle sind die Unternehmenswebsite, E-Commerce-Portale, Einzelhandelspartner und Direktvertrieb.',
    exampleTwo:
      'Mehrkanalstrategie mit Online-Plattform, Partnerschaften mit Sprachschulen und direktem B2B-Vertrieb.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION]: {
    name: 'Marktstrukturübersicht',
    instruction: 'Kommentieren Sie das Diagramm und nennen Sie die wichtigsten Segmente.',
    exampleOne:
      'Im Markt für landwirtschaftliche Maschinen gehören zu den wichtigsten Segmenten: Wettbewerber (John Deere etc.), Landwirte und Agrarbetriebe.',
    exampleTwo:
      'Zu den Schlüsselsegmenten gehören: freiberufliche Übersetzer, Agenturen, Technologiedienstleister, interne Übersetzungsabteilungen.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE]: CURRENT_MARKET_STRUCTURE_INSTRUCTIONS,

  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS]: {
    name: 'Wachstumsfaktoren',
    instruction:
      'Identifizieren Sie die wichtigsten Faktoren, die das Marktwachstum unterstützen – wirtschaftlich, technologisch, regulatorisch oder durch Konsumentenpräferenzen.',
    exampleOne:
      'Wesentliche Wachstumsfaktoren sind Nachhaltigkeit, technologische Entwicklungen und günstige Wirtschaftstrends.',
    exampleTwo:
      'Wachstum entsteht durch den Wandel zu Remote-Arbeit und virtuelle Zusammenarbeit, was die Nachfrage nach Übersetzungsdiensten erhöht.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT]: {
    name: 'Technologieeinfluss',
    instruction:
      'Bewerten Sie den Einfluss technologischer Veränderungen auf die Branche, einschließlich Innovationspotenzial und Effizienzsteigerungen.',
    exampleOne:
      'Automatisierung, Präzisionslandwirtschaft und Datenanalyse steigern die Effizienz in der Agrartechnik.',
    exampleTwo:
      'In der Sprachdienstleistungsbranche verbessern KI und maschinelles Lernen die Effizienz und Genauigkeit.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES]: {
    name: 'Preisstrategien',
    instruction:
      'Analysieren Sie die Preisstrategien der Wettbewerber und berücksichtigen Sie Modelle wie Value-based Pricing, Penetrationspreise und Kosten-plus.',
    exampleOne:
      'Wettbewerber nutzen verschiedene Preisstrategien wie Value-based und Penetrationspreise, oft mit Rabatten bei Großabnahmen.',
    exampleTwo:
      'Preisstrategien umfassen Stundenpreise, Wortpreise und Abomodelle von Tech-Anbietern.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE]: {
    name: 'Preisübersicht',
    instruction: 'Geben Sie einen klaren und präzisen Titel an, der den Inhalt des Diagramms korrekt widerspiegelt.',
    exampleOne: 'Preise für Ketten-Traktoren',
    exampleTwo: 'Preise für KI-gestützte Übersetzungswerkzeuge',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE]: {
    name: '',
    instruction: 'Geben Sie das tatsächliche Datum an, einschließlich Monat und Jahr.',
  },

  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART]: {
    [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_ROWS]: AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS,
  },
};

const KEY_COMPETITORS_INSTRUCTIONS = {
  [KEY_COMPETITORS_NAMES.COMPETITOR]: {
    name: 'Name',
    instruction: 'Geben Sie den vollständigen und offiziellen Namen des Unternehmens an.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [KEY_COMPETITORS_NAMES.FILE]: {
    name: '',
    instruction: 'Fügen Sie das Unternehmenslogo ein. Hohe Auflösung, transparente Hintergrund, passende Größe.',
    exampleOne: '',
    exampleTwo: '',
  },
  [KEY_COMPETITORS_NAMES.WEBSITE]: {
    name: 'Webseite',
    instruction: 'Geben Sie die vollständige Webadresse des Unternehmens an.',
    exampleOne: 'agro.tech.innovations.com',
    exampleTwo: 'translink.com',
  },
  [KEY_COMPETITORS_NAMES.LOCATION]: {
    name: 'Standort',
    instruction: 'Geben Sie Stadt und Land des offiziellen Firmensitzes an.',
    exampleOne: '134 E Main St, Marion, NC 28752, US',
    exampleTwo: 'Portland, US',
  },
  [KEY_COMPETITORS_NAMES.DESCRIPTION]: {
    name: 'Kurze Produktbeschreibung',
    instruction:
      'Beschreiben Sie die wichtigsten Produkteigenschaften und innovativen Merkmale, die sie im Markt hervorheben.',
    exampleOne:
      'Das Unternehmen bietet verschiedene landwirtschaftliche Maschinen wie Traktoren, Mähdrescher, Sämaschinen und Sprüher an.',
    exampleTwo:
      'Der Wettbewerber bietet eine fortschrittliche Plattform für Sprachdienstleistungen mit Tools zur Echtzeit-Zusammenarbeit.',
  },
  [KEY_COMPETITORS_NAMES.POSITIONING]: {
    name: 'Positionierung',
    instruction:
      'Beschreiben Sie die Marktposition der Wettbewerber, einschließlich Markenbekanntheit und Einfluss.',
    exampleOne:
      'Das Unternehmen positioniert sich als Hersteller nachhaltiger und umweltfreundlicher landwirtschaftlicher Maschinen.',
    exampleTwo:
      'Das Unternehmen ist für seine etablierte Marke und sein breites Übersetzernetzwerk bekannt.',
  },
  [KEY_COMPETITORS_NAMES.SALES_CHANNELS]: {
    name: 'Vertriebskanäle',
    instruction:
      'Geben Sie einen Überblick über die Vertriebskanäle der wichtigsten Wettbewerber.',
    exampleOne: 'Händlernetzwerke, Online-Plattformen, Direktvertrieb.',
    exampleTwo: 'Verkauf über Website, Branchenplattformen und E-Mail-Marketing.',
  },
  [KEY_COMPETITORS_NAMES.PRICING_POLICY]: {
    name: 'Preisstrategie',
    instruction:
      'Geben Sie einen präzisen Überblick über die Preisstrategien der Wettbewerber.',
    exampleOne:
      'Das Unternehmen verfolgt eine wettbewerbsorientierte Preisstrategie mit Fokus auf Preis-Leistungs-Verhältnis.',
    exampleTwo:
      'Der Wettbewerber nutzt dynamische Preisgestaltung abhängig von Nachfrage und Komplexität der Aufgaben.',
  },
  [KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME]: {
    name: 'Jährliches Verkaufsvolumen',
    instruction:
      'Geben Sie das jährliche Verkaufsvolumen bzw. den Umsatz des letzten Geschäftsjahres an.',
    exampleOne: '5 000 Einheiten für 25 Mio. USD',
    exampleTwo: '7 Mio. USD',
  },
  [KEY_COMPETITORS_NAMES.MARKET_SHARE]: {
    name: 'Marktanteil',
    instruction: 'Analysieren Sie Marktdaten, um den Marktanteil des Unternehmens zu bestimmen.',
    exampleOne: '0,02 %',
    exampleTwo: '13 %',
  },
  [KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL]: {
    name: 'Kundenloyalität',
    instruction:
      'Geben Sie das Niveau der Kundenloyalität an: hoch, mittel oder niedrig.',
    exampleOne: 'hoch',
    exampleTwo: 'mittel',
  },
};

const COMPETITORS_STRENGTHS = {
  [COMPANY_QUALITIES_NAMES.STRENGTH]: {
    name: 'Stärke',
    instruction:
      'Identifizieren Sie Kernkompetenzen und Ressourcen, die Wettbewerbsvorteile bieten.',
    exampleOne: ul([
      'Innovative Nutzung von Biokraftstofftechnologie in der Traktorproduktion',
      'Produktionsstandort in der Nähe eines Eisenbahnknotens',
      'Zertifikat für Umweltkonformität',
    ]),
    exampleTwo: ul([
      'Ein umfangreiches Netzwerk professioneller Übersetzer',
      'Sichere Zahlungssysteme',
      'Tools für Echtzeit-Zusammenarbeit',
    ]),
  },
};

const COMPETITORS_WEAKNESSES = {
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: {
    name: 'Schwäche',
    instruction:
      'Identifizieren Sie die internen Einschränkungen und Herausforderungen des Unternehmens. Beschreiben Sie transparent die Bereiche, die verbessert werden müssen: Prozesse, Ressourcen, Kompetenzen. Gehen Sie auf Abweichungen von Branchenstandards, operative Ineffizienzen und deren Auswirkungen auf Leistung und Wettbewerbsfähigkeit ein.',
    exampleOne: ul(['Begrenzte Produktpalette', 'Höhere Produktionskosten im Zusammenhang mit umweltfreundlicher Technologie']),
    exampleTwo: ul([
      'Veraltete Plattformoberfläche',
      'Höhere Preise im Vergleich zu Wettbewerbern',
      'Begrenzte Anzahl an Suchwerkzeugen für Übersetzer',
    ]),
  },
};

const COMPETITORS_QUALITIES_INSTRUCTIONS = {
  [COMPANY_QUALITIES_NAMES.NAME]: {
    name: 'Name',
    instruction: 'Geben Sie den vorgesehenen vollständigen und offiziellen Namen des Unternehmens an.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [COMPANY_QUALITIES_NAMES.FILE]: {
    name: 'Logo',
    instruction:
      'Fügen Sie das Unternehmenslogo ein. Stellen Sie sicher, dass das Logo hochauflösend ist, eine geeignete Größe hat und einen transparenten Hintergrund besitzt.',
    exampleOne: '',
    exampleTwo: '',
  },
  [COMPANY_QUALITIES_NAMES.STRENGTH]: COMPETITORS_STRENGTHS,
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: COMPETITORS_WEAKNESSES,
};

const COMPARISON_ITEM_INSTRUCTIONS = {
  [COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON]: {
    [FEATURES_FOR_COMPARISON_NAMES.FEATURE]: {
      name: 'Merkmal',
      instruction:
        'Stellen Sie die Unterscheidungsmerkmale und Vorteile vor, die Ihr Produkt oder Ihre Dienstleistung von anderen abheben. Erläutern Sie klar, was Ihr Angebot überlegen macht.',
      exampleOne: 'PS-Leistung',
      exampleTwo: 'Durchschnittliche Bearbeitungszeit',
    },
  },
  [FEATURES_LIST_NAMES.NAME]: {
    name: 'Name',
    instruction: 'Geben Sie den vorgesehenen vollständigen und offiziellen Namen des Unternehmens an.',
    exampleOne: 'John Deere',
    exampleTwo: 'LinguaWorld',
  },
  [FEATURES_LIST_NAMES.FILE]: {
    name: '',
    instruction:
      'Fügen Sie das Unternehmenslogo ein. Stellen Sie sicher, dass das Logo hochauflösend ist, eine geeignete Größe hat und einen transparenten Hintergrund besitzt.',
    exampleOne: '',
    exampleTwo: '',
  },
  [FEATURES_LIST_NAMES.FEATURE]: {
    name: 'Merkmalswert',
    instruction: `Quantifizieren Sie den Nutzen Ihres Produkts oder Ihrer Dienstleistung für die Kunden, indem Sie messbare Ergebnisse, Kosteneinsparungen oder Effizienzsteigerungen hervorheben.`,
    exampleOne: '200',
    exampleTwo: '20 Stunden',
  },
  [COMPETITORS_COMPARISON_NAMES.COMPETITOR_FEATURES_LIST]: {
    [FEATURES_LIST_NAMES.FILE]: {
      name: '',
      instruction:
        'Fügen Sie das Unternehmenslogo ein. Stellen Sie sicher, dass das Logo hochauflösend ist, eine geeignete Größe hat und einen transparenten Hintergrund besitzt.',
      exampleOne: '',
      exampleTwo: '',
    },
    [FEATURES_LIST_NAMES.NAME]: {
      name: 'Name',
      instruction: 'Geben Sie den vorgesehenen vollständigen und offiziellen Namen des Unternehmens an.',
      exampleOne: 'John Deere',
      exampleTwo: 'LinguaWorld',
    },
    [FEATURES_LIST_NAMES.FEATURE]: {
      name: 'Merkmalswert',
      instruction: `Quantifizieren Sie den Nutzen Ihres Produkts oder Ihrer Dienstleistung für die Kunden, indem Sie messbare Ergebnisse, Kosteneinsparungen oder Effizienzsteigerungen hervorheben.`,
      exampleOne: '200',
      exampleTwo: '20 Stunden',
    },
  },
};

const COMPETITIVE_ANALYSIS_INSTRUCTIONS = {
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS]: {
    name: 'Wettbewerberidentifikation',
    instruction:
      'Listen Sie direkte und indirekte Wettbewerber auf und erläutern Sie deren Relevanz. Heben Sie Gemeinsamkeiten bei Angeboten, Zielmärkten und Vertriebskanälen hervor und betonen Sie Wettbewerbs­vorteile sowie Marktpositionierung.',
    exampleOne:
      'Direkte Wettbewerber AgroTech und Harvest Dynamics bieten ähnliche schwere Traktoren an. Indirekte Wettbewerber sind GHY, das Zweirad-Traktoren und alternative Lösungen anbietet. Diese Wettbewerber bedienen denselben Zielmarkt der Landwirte und nutzen ähnliche Vertriebskanäle.',
    exampleTwo:
      'Zu den direkten Wettbewerbern gehören TransLink und LinHub, die vielfältige Sprachdienstleistungen anbieten, maschinelle Übersetzungstechnologien nutzen und sich auf Privatkunden konzentrieren. Indirekte Wettbewerber sind Plattformen zur Suche nach Freiberuflern, die ebenfalls Sprachdienste anbieten.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS]: {
    name: 'Kundengewinnung',
    instruction:
      'Definieren Sie Strategien, um Kunden von Wettbewerbern zu gewinnen. Heben Sie einzigartige Wertangebote, Preisvorteile und Werbemaßnahmen hervor, die Kunden anziehen und die Differenzierung sowie Bindung sicherstellen.',
    exampleOne:
      'Das Unternehmen wird wettbewerbsfähige Preise, Einführungsrabatte und Eintauschprämien anbieten. Zudem wird es in digitalen Marketingmaßnahmen, Branchenveranstaltungen und strategische Partnerschaften investieren.',
    exampleTwo:
      'Um Kunden von Wettbewerbern zu gewinnen, führt das Unternehmen eine umfangreiche PR-Kampagne in sozialen Medien durch und kooperiert mit Bloggern. Die stärkere Markenpräsenz soll die Position des Unternehmens festigen und zusätzliche Kunden anziehen.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM]: COMPARISON_ITEM_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS]: KEY_COMPETITORS_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE]: {
    name: 'Produktdifferenzierung',
    instruction:
      'Vergleichen Sie konkurrierende Produkte/Dienstleistungen hinsichtlich Qualität und Merkmalen. Heben Sie Stärken wie Haltbarkeit, Leistung oder einzigartige Funktionen hervor. Betonen Sie die Vorteile Ihres Angebots, um sich klar von Wettbewerbern abzuheben.',
    exampleOne:
      'Wettbewerber bieten verschiedene Traktortypen mit individuellen Merkmalen an. Einige setzen auf ökologische Biokraftstoff-Traktoren oder spezielle Materialien. Die Traktoren des Unternehmens zeichnen sich durch ein zuverlässiges Design aus, das eine überlegene Leistung unter schwierigen Bedingungen und Bodenverhältnissen gewährleistet.',
    exampleTwo:
      'Wettbewerber überzeugen durch starke Markenbekanntheit und umfangreiche Werbung, die eine breite Kundenbasis anzieht. Die Plattform des Unternehmens hebt sich durch integrierte KI-Technologie hervor, die Übersetzungsgenauigkeit und -geschwindigkeit verbessert und so eine überlegene Leistung und Effizienz im Vergleich zu Wettbewerbern bietet.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS]: {
    name: 'Kaufmotivation',
    instruction:
      'Analysieren Sie die Faktoren, die Kunden zu Wettbewerbsprodukten führen. Berücksichtigen Sie Preis, Markenreputation, Funktionsumfang und Kundenservice. Identifizieren Sie Verbesserungsbereiche, um Kundenbedürfnisse und -präferenzen besser zu erfüllen.',
    exampleOne:
      'Kunden kaufen Produkte von Wettbewerbern aufgrund etablierter Markenreputation, wettbewerbsfähiger Preise und breiter Funktionsvielfalt. Um Kundenbedürfnisse besser zu erfüllen, können optimierte Preisstrategien, stärkere Markenpräsenz und einzigartige Produktmerkmale hilfreich sein.',
    exampleTwo:
      'Kunden können sich aufgrund niedrigerer Preise und einer bekannten Marke für Wettbewerbsangebote entscheiden. Um effektiv zu konkurrieren, muss das Unternehmen seinen Mehrwert durch wettbewerbsfähige Preise und einen verbesserten Kundensupport steigern.',
  },
  // [COMPETITIVE_ANALYSIS_NAMES.PRODUCT]: {
  //   name: '',
  //   instruction: '',
  //   exampleOne: '',
  //   exampleTwo: '',
  // },
  [COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS]: {
    name: 'Zukünftiger Wettbewerb',
    instruction:
      'Bewerten Sie potenzielle neue Marktteilnehmer. Berücksichtigen Sie Branchenattraktivität, Markteintrittsbarrieren und Marktlücken. Heben Sie Risiken wie verstärkten Wettbewerb, Preiskämpfe und mögliche Verluste von Marktanteilen hervor.',
    exampleOne:
      'Technologische Entwicklungen und sich verändernde Kundenpräferenzen werden den zukünftigen Wettbewerb beeinflussen. Globalisierung und wachsende Märkte können zu vermehrtem Wettbewerb führen. Die starke Marktpräsenz und Markenbekanntheit etablierter Akteure können für neue Unternehmen zusätzliche Eintrittsbarrieren schaffen.',
    exampleTwo:
      'Potenzielle Marktteilnehmer sehen sich zwar geringen formalen Eintrittsbarrieren aufgrund der globalen Natur des Marktes gegenüber, müssen jedoch mit intensivem Wettbewerb rechnen. Die größte Herausforderung besteht darin, eine eigene Marke zwischen etablierten Wettbewerbern aufzubauen und innovative Lösungen zu entwickeln, um Marktlücken zu schließen.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.BARRIERS]: {
    name: 'Markteintrittsbarrieren',
    instruction:
      'Identifizieren Sie Markteintrittsbarrieren. Diskutieren Sie Faktoren wie hohe Kapitalanforderungen, regulatorische Hürden, Markenloyalität und Skaleneffekte, die neue Marktteilnehmer abschrecken können.',
    exampleOne:
      'Zu den Eintrittsbedingungen gehören hohe Kapitalanforderungen für F&E und Produktion. Die starke Marktpräsenz und Markenbekanntheit bestehender Wettbewerber können zusätzliche Barrieren für neue Unternehmen darstellen.',
    exampleTwo:
      'Neue Marktteilnehmer stehen vor Herausforderungen wie hohen Anfangsinvestitionen in technische Infrastruktur und dem Aufbau eines verlässlichen Übersetzernetzwerks.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET]: {
    name: 'Marktverändernde Faktoren',
    instruction:
      'Antizipieren Sie potenzielle Marktstörer (Disruptoren). Bewerten Sie neue Technologien, sich wandelnde Kundenpräferenzen und regulatorische Veränderungen. Heben Sie Innovationen hervor, die die Branchenstruktur verändern und bestehende Marktteilnehmer bedrohen könnten.',
    exampleOne:
      'Das Aufkommen vollständig autonomer, KI-gesteuerter Traktoren stellt eine erhebliche Bedrohung für bestehende Marktteilnehmer dar. Diese Innovation kann durch die Reduktion des Bedarfs an menschlicher Arbeitskraft die Marktstrukturen grundlegend verändern.',
    exampleTwo:
      'Die Einführung KI-gestützter Simultanübersetzung stellt eine bedeutende Disruption dar, die die Nachfrage nach traditionellen Sprachdiensten reduzieren kann. Diese Technologie bietet schnellere und kostengünstigere Lösungen und zwingt etablierte Anbieter zur Anpassung.',
  },
};

const CONSUMER_NEEDS_INSTRUCTIONS = {
  [CONSUMER_NEEDS_NAMES.B2C]: {
    [B2C_NAMES.GENDER]: {
      name: 'Geschlecht',
      instruction:
        'Geben Sie das Geschlecht der Endverbraucher an und machen Sie deutlich, ob es sich überwiegend um männliche, weibliche oder geschlechtsneutrale Zielgruppen handelt.',
      exampleOne: '',
      exampleTwo: '',
    },
    [B2C_NAMES.AGE]: {
      name: 'Alter',
      instruction:
        'Bestimmen Sie die Altersgruppe der Verbraucher anhand von demografischen Daten und Marktforschung. Stellen Sie sicher, dass die Spanne für die Zielgruppe relevant ist.',
      exampleOne: '25–65',
      exampleTwo: '18–45',
    },
    [B2C_NAMES.ANNUAL_INCOME]: {
      name: 'Einkommen',
      instruction:
        'Bestimmen Sie das Einkommensniveau der Verbraucher anhand von Marktdaten und Indikatoren zur Kaufkraft. Achten Sie auf Relevanz für die Marktsegmentierung.',
      exampleOne: 'ab 7.000 USD',
      exampleTwo: '2.000–10.000 USD',
    },
    [B2C_NAMES.PROFESSION]: {
      name: 'Beruf',
      instruction:
        'Bestimmen Sie die typischen Berufe der Verbraucher durch Marktforschung und demografische Analyse. Stellen Sie sicher, dass die identifizierten Berufsgruppen zur Zielgruppe passen.',
      exampleOne: 'Landwirt',
      exampleTwo: 'Studierende, Mitarbeiter in multinationalen Unternehmen, Wissenschaft und Tourismusbranche',
    },
    [B2C_NAMES.PERSONAL_INTERESTS]: {
      name: 'Persönliche Interessen',
      instruction:
        'Beschreiben Sie die persönlichen Interessen der Zielgruppe. Nutzen Sie demografische Erkenntnisse, um Hobbys, Präferenzen und Lebensstile zu identifizieren, die Kaufentscheidungen beeinflussen.',
      exampleOne: 'Unter anderem Landwirtschaft, Maschinen, Technologie, Umweltschutz, Bodengesundheit.',
      exampleTwo: 'Interesse an fremden Kulturen, Technologie und effizienter Kommunikation',
    },
    [B2C_NAMES.PURCHASING_HABITS]: {
      name: 'Kaufgewohnheiten',
      instruction:
        'Analysieren Sie die Kaufgewohnheiten der Zielgruppe. Nutzen Sie Verbraucherdaten, um Kaufhäufigkeit, bevorzugte Kanäle und entscheidende Einflussfaktoren zu identifizieren.',
      exampleOne:
        'Kunden kaufen typischerweise 1–2 Einheiten alle 7 Jahre. Entscheidungen werden von Wirtschaftlichkeit, Haltbarkeit und Leistung beeinflusst, wobei die bevorzugten Kanäle nach Zugänglichkeit und Komfort variieren.',
      exampleTwo: 'Sie kaufen Dienstleistungen online und bevorzugen Abonnementmodelle.',
    },
    [B2C_NAMES.NEEDS_AND_PROBLEMS]: {
      name: 'Bedürfnisse und Probleme',
      instruction:
        'Identifizieren Sie die Bedürfnisse und Probleme der Zielgruppe. Nutzen Sie Marktforschung, um Pain Points und ungedeckte Bedürfnisse zu erkennen und Lösungen zu formulieren, die diese Herausforderungen effektiv adressieren.',
      exampleOne:
        'Kunden, die auf schweren und nassen Böden arbeiten, benötigen zuverlässige und langlebige Traktoren, die unter schwierigen Bedingungen effizient arbeiten, ohne den Boden zu schädigen.',
      exampleTwo:
        'Sie stehen vor Sprachbarrieren, die eine effektive Kommunikation erschweren. Die Zielgruppe sucht eine Online-Lösung, die schnelle und zuverlässige Sprachunterstützung bietet, um sich in verschiedenen Sprachumgebungen sicher zu bewegen.',
    },
  },
  [CONSUMER_NEEDS_NAMES.B2B]: {
    [B2B_NAMES.INDUSTRY]: {
      name: 'Branche',
      instruction:
        'Bestimmen Sie die Branche Ihres idealen Geschäftskunden. Wählen Sie den passendsten Sektor aus oder beschreiben Sie die Branche, falls sie nicht in den vorgegebenen Optionen enthalten ist. Geben Sie die Region an, in der die Hauptaktivitäten Ihrer Geschäftskunden angesiedelt sind.',
      exampleOne: 'Landwirtschaft',
      exampleTwo: 'Multinationale Unternehmen, Technologie, Gesundheitswesen, Rechtswesen, Bildung',
    },
    [B2B_NAMES.ANNUAL_REVENUE]: {
      name: 'Jahresumsatz',
      instruction:
        'Geben Sie die jährliche Umsatzspanne Ihres typischen Geschäftskunden an. Bestimmen Sie diese auf Basis von Finanzberichten und Einkommensanalysen.',
      exampleOne: '60–120 Mio. USD',
      exampleTwo: '10–500 Mio. USD',
    },
    [B2B_NAMES.NUMBER_OF_EMPLOYERS]: {
      name: 'Anzahl der Mitarbeiter',
      instruction:
        'Geben Sie die übliche Anzahl der Mitarbeiter Ihres typischen Geschäftskunden an. Schließen Sie Vollzeit-, Teilzeit- und Vertragskräfte ein.',
      exampleOne: '300–900',
      exampleTwo: '250+',
    },
    [B2B_NAMES.REGION]: {
      name: 'Region',
      instruction:
        'Nennen Sie die wichtigste geografische Region oder den Markt, in dem Ihr typischer Geschäftskunde tätig ist. Falls mehrere Regionen relevant sind, listen Sie die wichtigsten auf.',
      exampleOne: 'USA',
      exampleTwo: 'Nordamerika, Europa, Asien-Pazifik',
    },
    [B2B_NAMES.PURCHASING_CYCLE]: {
      name: 'Kaufzyklus',
      instruction:
        'Beschreiben Sie das Kaufverhalten Ihres idealen Geschäftskunden, einschließlich Häufigkeit und Zeitpunkt größerer Investitionsentscheidungen.',
      exampleOne: 'Alle 5–7 Jahre',
      exampleTwo: 'Vierteljährlich/Jährlich',
    },
  },
  [CONSUMER_NEEDS_NAMES.PURCHASING_DECISION]: {
    name: 'Kaufentscheidungen',
    instruction:
      'Wenn Ihre Kunden juristische Personen sind, erläutern Sie die Entscheidungsstruktur in diesen Organisationen. Geben Sie an, wer die Kaufentscheidung trifft, und heben Sie gegebenenfalls die Rolle von Einflussnehmern oder weiteren Stakeholdern hervor.',
    exampleOne:
      'In der Regel treffen Eigentümer oder das obere Management Kaufentscheidungen. Fachexperten für Technik können den Prozess durch Empfehlungen beeinflussen.',
    exampleTwo:
      'Kaufentscheidungen werden typischerweise von Abteilungsleitern oder Einkaufsmanagern in Unternehmen sowie von Projektleitern oder Verwaltungsbeamten in Behörden getroffen.',
  },
  [CONSUMER_NEEDS_NAMES.INFLUENCING_FACTORS]: {
    name: 'Einflussfaktoren',
    instruction:
      'Identifizieren und erläutern Sie die wichtigsten Faktoren, die den Kaufentscheidungsprozess Ihrer Kunden beeinflussen. Dazu gehören zum Beispiel Produkteigenschaften, Preis, Markenreputation, Kundenbewertungen oder spezifische Bedürfnisse, die Ihr Produkt oder Ihre Dienstleistung adressiert.',
    exampleOne:
      'Zuverlässigkeit, Haltbarkeit, Leistung und Wirtschaftlichkeit sind Schlüsselfaktoren. Kunden priorisieren Merkmale, die ihren konkreten Anforderungen entsprechen, etwa den Einsatz auf schweren und nassen Böden.',
    exampleTwo:
      'Zu den wichtigsten Einflussfaktoren zählen Servicequalität, Sprachabdeckung, Integrationsfähigkeit, Reputation des Anbieters und Flexibilität.',
  },
  [CONSUMER_NEEDS_NAMES.DIFFERENCES]: {
    name: 'Verhaltensunterschiede',
    instruction:
      'Stellen Sie wesentliche Unterschiede im Verhalten oder in den Präferenzen verschiedener Kundensegmente dar. Heben Sie Faktoren hervor wie Kaufgewohnheiten, Produktpräferenzen oder Reaktionen auf Marketingmaßnahmen.',
    exampleOne:
      'Landwirtschaftliche Unternehmen bevorzugen Großbestellungen und legen Wert auf Effizienz, während private Betriebe eher Einzelgeräte kaufen und Zuverlässigkeit priorisieren. Diese Unterschiede im Kaufverhalten beeinflussen die Produktionsplanung entsprechend der Nachfrage.',
    exampleTwo:
      'Unternehmen legen Wert auf schnelle Übersetzungen, während Behörden umfassende Sprachlösungen für offizielle Dokumente priorisieren. Unternehmen reagieren gut auf zielgerichtetes Marketing mit Fokus auf Genauigkeit und Schnelligkeit, während Behörden Glaubwürdigkeit und Sicherheit höher gewichten.',
  },
  [CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED]: {
    name: 'Marktzufriedenheit',
    instruction:
      'Geben Sie einen Überblick über das aktuelle Zufriedenheitsniveau der Kunden im Markt. Beschreiben Sie die allgemeine Stimmung gegenüber bestehenden Angeboten und Dienstleistungen. Heben Sie Bereiche hervor, in denen Kunden zufrieden sind, sowie jene, in denen Verbesserungsbedarf besteht.',
    exampleOne:
      'Die Zufriedenheit mit bestehenden Produkten variiert. Einige Kunden sind mit Funktionen und Leistung zufrieden, andere wünschen sich Verbesserungen bei Haltbarkeit und Preisgestaltung. Insgesamt zeigt sich ein gemischtes Bild aus Zufriedenheit und dem Wunsch nach Optimierungen.',
    exampleTwo:
      'Die Zufriedenheit mit bestehenden Dienstleistungen ist moderat. Einige Kunden kritisieren komplexe Registrierungsprozesse und eingeschränkte Zugänglichkeit. Eine Vereinfachung der Abläufe und höhere Benutzerfreundlichkeit könnten die Gesamtzufriedenheit deutlich steigern.',
  },
  [CONSUMER_NEEDS_NAMES.DEMAND]: {
    name: 'Nachfrageentwicklung',
    instruction:
      'Analysieren Sie, ob das Unternehmen in einem Markt mit bereits etablierter Nachfrage tätig ist oder ob es durch innovative Produkte oder Dienstleistungen neue Nachfrage schafft.',
    exampleOne:
      'Das Unternehmen ist in einem Markt mit etablierter Nachfrage tätig. Traktoren sind essenzielle landwirtschaftliche Geräte, und der anhaltende Bedarf an Mechanisierung treibt die Nachfrage. Innovationen finden innerhalb eines bestehenden Marktes statt.',
    exampleTwo:
      'Das Unternehmen schafft neue Nachfrage, indem es eine Online-Plattform zur Suche nach Übersetzern einführt. Dieser Dienst ist im Markt noch nicht etabliert und bietet eine einzigartige Lösung für bestehende Kundenbedürfnisse.',
  },
  [CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE]: {
    name: 'Größe der Kundenbasis',
    instruction:
      'Geben Sie einen Überblick über die aktuelle Kundenbasis und heben Sie die Anzahl der gewonnenen Kunden hervor. Beschreiben Sie das Interesse am Produkt anhand von Indikatoren wie Anfragen, Registrierungen oder Vorbestellungen.',
    exampleOne:
      'Das Unternehmen hat über 800 landwirtschaftliche Betriebe als Kunden. Im Jahr 2022 stieg die Kundenzahl im Vergleich zum Vorjahr um 12 %, was ein wachsendes Interesse und eine höhere Kundenbindung widerspiegelt. Zusätzlich sind 40 % der Kunden Wiederholungskäufer.',
    exampleTwo:
      'Die Plattform verfügt derzeit über 20.000 registrierte Übersetzer weltweit und verzeichnet monatlich rund 100 neue Registrierungen. Im Jahr 2022 wurden 50.000 Aufträge abgewickelt, was einem Anstieg von 23 % gegenüber dem Vorjahr entspricht.',
  },
  [CONSUMER_NEEDS_NAMES.SURVEY]: {
    name: 'Feedback zur Produkteinführung',
    instruction:
      'Stellen Sie die Kundenbefragung vor, einschließlich Zweck und Umfang. Geben Sie die Zielgruppe und die verwendete Methodik an. Fassen Sie die wichtigsten Fragen kurz zusammen und präsentieren Sie die zentralen Ergebnisse mit Blick auf Trends, Präferenzen und Einstellungen potenzieller Kunden.',
    exampleOne:
      'Die Kundenbefragung dient der Bewertung der Zufriedenheit nach dem Kauf. Sie richtet sich an jüngste Käufer und erfolgt telefonisch. Die Ergebnisse zeigen, dass 80 % der Kunden zufrieden sind und einen erneuten Kauf in Betracht ziehen.',
    exampleTwo:
      'Das Unternehmen führt eine Online-Umfrage zur Kundenzufriedenheit durch, um Verbesserungspotenziale zu identifizieren. Die Ergebnisse zeigen den Wunsch nach zusätzlichen Zahlungsoptionen und einer mobilen App, was konkrete Ansatzpunkte für Weiterentwicklungen liefert.',
  },
};

export const MARKET_ANALYSIS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW]: MARKET_OVERVIEW_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS]: COMPETITIVE_ANALYSIS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.CONSUMER_NEEDS]: CONSUMER_NEEDS_INSTRUCTIONS,
};
